import { action, observable, reaction, runInAction } from 'mobx';
import { FacilityEditForm } from 'application/facility/forms';
import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { applicationContext } from 'core/utils';
import { ModalParams } from 'core/models';
import { LookupService } from "common/services";
import { FacilityService, BranchService, CoverageTypeService, FacilityAdditionalContactService } from 'application/facility/services';
import _ from 'lodash';
import { ProductService } from 'application/product/services';
import { PersonService } from 'application/person/services';
import { CorporateService } from 'application/corporate/services';
import { ESignPreferenceService } from 'application/esign-preference/services';

@applicationContext
class FacilityEditViewStore extends BaseEditViewStore {
    @observable accessor areasConstructed = false;
    @observable accessor additionalContacts = null;
    @observable accessor isSavingResource = false;
    @observable accessor isAddressSynchronization = false;
    @observable accessor isChanged = false;
    @observable accessor isSyncing;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const service = new FacilityService(rootStore.application.baasic.apiClient);
        const branchService = new BranchService(rootStore.application.baasic.apiClient);
        const personService = new PersonService(rootStore.application.baasic.apiClient);
        const coverageTypeService = new CoverageTypeService(rootStore.application.baasic.apiClient);
        const corporateService = new CorporateService(rootStore.application.baasic.apiClient);
        const productService = new ProductService(rootStore.application.baasic.apiClient);
        const lookupService = new LookupService(rootStore.application.baasic.apiClient);
        const eSignpreferenceService = new ESignPreferenceService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'facility-edit',
            id: id,
            Form: FacilityEditForm,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await service.get(id, { embed: 'FacilityAreaOfCares,FacilityAdditionalContact,PumpBrand,AccountRep,BillingRep,DocumentRep,PayorRep,IntakeRep,WoundCareRep,InitialIntakeRep,ConfirmingRep,InsuranceReviewRep,PurchaseRep,Branch,Corporate,PumpBrand,SpikeSet,Syringe,DrainageBag', });
                        this.rootStore.viewStore.setNavigationOptions({ title: response.data.name });
                        const sortedContacts = _.orderBy(response.data.facilityAdditionalContact, ["inActive", c => c.name.toLowerCase()], "asc");
                        const mappedContacts = sortedContacts.map(additionalContacts => {
                            let contactDepartments = additionalContacts.facilityAdditionalContactContactDepartments &&
                                additionalContacts.facilityAdditionalContactContactDepartments.map(i => i.contactDepartment.name).join(", ");
                            return {
                                ...additionalContacts,
                                departments: contactDepartments
                            }
                        });
                        runInAction(() => {
                            this.additionalContacts = mappedContacts;
                        });

                        response.data.facilityAreaOfCares = _.sortBy(response.data.facilityAreaOfCares, a => a.areaOfCare.name);
                        await this.getLookups();
                        return response.data;
                    },
                    update: async (resource) => {
                        let areaOfCares = _.map(this.item.facilityAreaOfCares, item => {
                            return _.extend(item, resource.areaOfCares["item-" + item.id], { facilityId: id });
                        });
                        resource.facilityAreaOfCares = areaOfCares;
                        await service.update(resource);
                    },
                    syncAddress: async (id) => {
                        return await service.syncAddress({ id: id });
                    }
                }
            },
            FormClass: FacilityEditForm,
        });

        this.facilityAdditionalContactService = new FacilityAdditionalContactService(rootStore.application.baasic.apiClient);

        this.facilityStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true,
        }, {
            fetchFunc: async (term) => {
                const response = await service.find({ searchQuery: term, rpp: 20, fields: "id,name" });
                return response.data.item;
            },
            onChange: async (id) => {
                window.location.replace(this.rootStore.routerStore.findRoute("master.app.main.facility.edit").pattern.replace(":id", id));
            }
        });

        this.mainVendorStore = new BaasicDropdownStore({
                filterable: false,
            },{
                fetchFunc: async () => {
                    const response = await lookupService.getLookups("Vendor");
                    return _.orderBy(response.vendor.filter(v=>v.abrv !== 'main-vendor'), 'name', 'asc')
                        .map(v=>({ id:v.id, name:v.name }));
                },
        });

        this.pricingGroupStore = new BaasicDropdownStore(
            {
                filterable: true,
            },
            {
                fetchFunc: async (term) => {
                    // ISG-2961: NikoHealth pricing groups may be unavailable; show the field empty rather than fail.
                    let response;
                    try {
                        response = await lookupService.getLookups("pricingGroup");
                        // eslint-disable-next-line no-unused-vars
                    } catch (err) {
                        return [];
                    }

                    return response["pricingGroup"]
                        .map(item => ({
                            id: item.id,
                            abrv: item.abrv,
                            name: item.name
                        }))
                        .filter(item => {
                            if (!term || term.trim() === "") return true;

                            return item.name.toLowerCase().includes(term.toLowerCase());
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));
                },
            }
        );

        this.branchStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await branchService.find({ searchQuery: term, rpp: 5, fields: "id,name" });
                return response.data.item;
            },
            onChange: (branch) => {
                this.item.branch = branch;
                this.form.set({ branch: branch });
            }
        });

        this.corporateStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await corporateService.find({ isActive: true, searchQuery: term, rpp: 20, fields: 'id,name' });

                const corpId = this.form.$('corporateId').value;
                const corp = response.data.item.filter(i => i.id === corpId);

                if (corp.length > 0) {
                    const corpIndex = response.data.item.findIndex((c) => c.id === corpId);
                    const tempCorp = response.data.item.splice(corpIndex, 1);

                    response.data.item.unshift(tempCorp[0]);
                }
                return _.orderBy(response.data.item, "name", "asc");
            }
        });

        this.jurisdictionStore = new BaasicDropdownStore({
            dataItemKey: 'name'
        }, {
            fetchFunc: async () => {
                return _.map(['A', 'B', 'C', 'D'], item => {
                    return { name: item };
                });
            }
        });

        this.cbaStore = new BaasicDropdownStore({
            dataItemKey: 'name'
        }, {
            fetchFunc: async () => {
                return _.map(['False', 'True'], item => {
                    return { name: item };
                });
            }
        });

        this.pumpBrandStore = new BaasicDropdownStore({
            initFetch: false
        }, {
            fetchFunc: async () => {
                return this.pumpBrand.map(x => {
                    return { id: x.id, name: x.name };
                });
            },
            onChange: () => {
                this.spikeSetStore.filterAsync();
                this.spikeSetStore.setValue(null);
            }
        });

        this.drainageBagStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                let drainageBagDefaultItemTypeId = this.defaultItemType.find(x => x.abrv === 'drainage-bag').id;

                const response = await productService.find({
                    sort: "itemId|asc",
                    rpp: 20,
                    fields: 'id,itemId',
                    isActive: true,
                    defaultItemTypeId: drainageBagDefaultItemTypeId,
                    searchQuery: term
                });
                return _.map(response.data.item, item => {
                    return {
                        id: item.id,
                        name: item.itemId
                    };
                });
            },
        });

        this.spikeSetStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                let spikeSetDefaultItemTypeId = this.defaultItemType.find(x => x.abrv === 'spike-set').id;

                let selectedPumpId = this.pumpBrandStore.value && this.pumpBrandStore.value.id;

                const response = await productService.find({
                    sort: "itemId|asc",
                    rpp: 20,
                    fields: 'id,itemId',
                    isActive: true,
                    pumpBrandId: selectedPumpId,
                    defaultItemTypeId: spikeSetDefaultItemTypeId,
                    searchQuery: term
                });

                return response.data.item.map(x => {
                    return {
                        id: x.id,
                        name: x.itemId
                    };
                });

            },
        });

        this.syringeStore = new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                let syringeDefaultItemTypeId = this.defaultItemType.find(x => x.abrv === 'syringe').id;
                const response = await productService.find({
                    sort: "itemId|asc",
                    rpp: 20,
                    fields: 'id,itemId',
                    isActive: true,
                    defaultItemTypeId: syringeDefaultItemTypeId,
                    searchQuery: term
                });
                return _.map(response.data.item, item => {
                    return {
                        id: item.id,
                        name: item.itemId
                    };
                });
            }
        });

        this.contactEditModal = new ModalParams({
            onSuccess: this.onSuccessUpdateAditionalContact
        });

        this.bulkEmrModal = new ModalParams({});
        this.facilityPayerValidationModal = new ModalParams({});

        this.additionalContactBulkCreateModal = new ModalParams({
            onSuccess: this.onSuccessUpdateAditionalContact
        });

        this.service = service;
        this.personService = personService;
        this.coverageTypeService = coverageTypeService;
        this.lookupService = lookupService;
        this.eSignPreferenceService = eSignpreferenceService;
        this.accountRepStore = this.createPersonDropdownStore(this.onAccountRepChange);
        this.purchaseRepStore = this.createPersonDropdownStore(this.onPurchaseRepChange);
        this.intakeRepStore = this.createPersonDropdownStore(this.onIntakeRepChange);
        this.woundCareRepStore = this.createPersonDropdownStore(this.onWoundCareRepChange);
        this.initialIntakeRepStore = this.createPersonDropdownStore(this.onInitialIntakeRepChange);
        this.insuranceReviewRepStore = this.createPersonDropdownStore(this.onInsuranceReviewRepChange);
        this.confirmingRepStore = this.createPersonDropdownStore(this.onConfirmingRepChange);
        this.payorRepStore = this.createPersonDropdownStore(this.onPayorRepChange);
        this.documentRepStore = this.createPersonDropdownStore(this.onDocumentRepChange);
        this.billingRepStore = this.createPersonDropdownStore(this.onBillingRepChange);
        this.eSignPreferenceStore = this.createESignPreferenceDropdownStore(this.eSignPreferenceChange);

        this.settings = rootStore.applicationStore.app.baasic.settings;

        this.addReactions();
    }

    @action.bound
    onUpdateError(error) {
        if (!this.errorActions.onUpdateError) {
            const message =
                error && error.data ? error.data :
                error && error.message ? error.message :
                "An error occurred";
            this.rootStore.notificationStore.error(message);
            return;
        }
        return this.errorActions.onUpdateError(error);
    }

    @action
    addReactions() {
        reaction(
			() => this.form.$("name").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("oldName").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("address").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("phone").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("accountRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("purchaseRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("intakeRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("woundCareRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("initialIntakeRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("confirmingRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("insuranceReviewRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("payorRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("documentRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("billingRepId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("jurisdiction").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("cba").value,
			(value) => {
				this.isChanged = true;
				if (value) {
					this.form.$("cba").set('placeholder', '');
					this.cbaStore.options.placeholder = '';
				}
			}
		);
        reaction(
			() => this.form.$("corporate").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("corporateId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("branchId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("masterPatientId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("fastcareId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("contactName").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("contactEmail").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("notes").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("notes").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("generalNote").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("emr").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("emrAccountName").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("emrUserName").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("emrPassword").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("areaOfCares").touched,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("inActive").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("isOnHold").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("partBOnly").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("pumpsNotSetInitially").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("doNotSendIVPoles").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("sendAgoraPo").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("isMonthlyTrachTube").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("documentationHold").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("isAutoSupplyEnfitAdapter").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("invoicedMonthly").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("shipPoint").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("isNew").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("isNewFlow2").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("requiredSecondPayor").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("deliveryNote").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("payorInformation").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("priceTable").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("shipPointUnited").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("marketingRep").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("pumpBrandId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("spikeSetId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("syringeId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("shippingVendor").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("uniqueCode").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("drainageBagId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("emergencySupplyDays").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("eSignPreferenceId").value,
			() => {
				this.isChanged = true;
			}
		);
        reaction(
			() => this.form.$("eSignNote").value,
			() => {
				this.isChanged = true;
			}
		);
    }

    @action
    async setItem(item) {
        this.item = item;
        this.itemInitialized = true;
        this.rootStore.titleStore.setTitle(this.item ? `Facility Edit - ${this.item.name}` : 'Facility Edit');
        await this.setDefaultStoreValue(item);
    }

    @action.bound
    async setDefaultStoreValue(data) {
        this.facilityStore.setValue(data);
        this.facilityStore.setItems([{ ...data }]);

        this.accountRepStore.setValue(data.accountRep && { id: data.accountRep.id, name: data.accountRep.firstName + ' ' + data.accountRep.lastName });
        this.billingRepStore.setValue(data.billingRep && { id: data.billingRep.id, name: data.billingRep.firstName + ' ' + data.billingRep.lastName });
        this.documentRepStore.setValue(data.documentRep && { id: data.documentRep.id, name: data.documentRep.firstName + ' ' + data.documentRep.lastName });
        this.payorRepStore.setValue(data.payorRep && { id: data.payorRep.id, name: data.payorRep.firstName + ' ' + data.payorRep.lastName });
        this.intakeRepStore.setValue(data.intakeRep && { id: data.intakeRep.id, name: data.intakeRep.firstName + ' ' + data.intakeRep.lastName });
        this.woundCareRepStore.setValue(data.woundCareRep && { id: data.woundCareRep.id, name: data.woundCareRep.firstName + ' ' + data.woundCareRep.lastName });
        this.initialIntakeRepStore.setValue(data.initialIntakeRep && { id: data.initialIntakeRep.id, name: data.initialIntakeRep.firstName + ' ' + data.initialIntakeRep.lastName });
        this.insuranceReviewRepStore.setValue(data.insuranceReviewRep && { id: data.insuranceReviewRep.id, name: data.insuranceReviewRep.firstName + ' ' + data.insuranceReviewRep.lastName });
        this.confirmingRepStore.setValue(data.confirmingRep && { id: data.confirmingRep.id, name: data.confirmingRep.firstName + ' ' + data.confirmingRep.lastName });
        this.purchaseRepStore.setValue(data.purchaseRep && { id: data.purchaseRep.id, name: data.purchaseRep.firstName + ' ' + data.purchaseRep.lastName });

        this.corporateStore.setValue(data.corporate_);
        this.branchStore.setValue(data.branch);

        this.pumpBrandStore.setValue(data.pumpBrand_ && { id: data.pumpBrand_.id, name: data.pumpBrand_.itemId });
        this.spikeSetStore.setValue(data.spikeSet_ && { id: data.spikeSet_.id, name: data.spikeSet_.itemId });
        this.syringeStore.setValue(data.syringe_ && { id: data.syringe_.id, name: data.syringe_.itemId });
        this.drainageBagStore.setValue(data.drainageBag_ && { id: data.drainageBag_.id, name: data.drainageBag_.itemId });

        data.accountRep ? this.accountRepStore.setItems([{ id: data.accountRep.id, name: data.accountRep.firstName + ' ' + data.accountRep.lastName }]) : await this.accountRepStore.filterAsync();
        data.billingRep ? this.billingRepStore.setItems([{ id: data.billingRep.id, name: data.billingRep.firstName + ' ' + data.billingRep.lastName }]) : await this.billingRepStore.filterAsync();
        data.documentRep ? this.documentRepStore.setItems([{ id: data.documentRep.id, name: data.documentRep.firstName + ' ' + data.documentRep.lastName }]) : await this.documentRepStore.filterAsync();
        data.payorRep ? this.payorRepStore.setItems([{ id: data.payorRep.id, name: data.payorRep.firstName + ' ' + data.payorRep.lastName }]) : await this.payorRepStore.filterAsync();
        data.intakeRep ? this.intakeRepStore.setItems([{ id: data.intakeRep.id, name: data.intakeRep.firstName + ' ' + data.intakeRep.lastName }]) : await this.intakeRepStore.filterAsync();
        data.woundCareRep ? this.woundCareRepStore.setItems([{ id: data.woundCareRep.id, name: data.woundCareRep.firstName + ' ' + data.woundCareRep.lastName }]) : await this.woundCareRepStore.filterAsync();
        data.initialIntakeRep ? this.initialIntakeRepStore.setItems([{ id: data.initialIntakeRep.id, name: data.initialIntakeRep.firstName + ' ' + data.initialIntakeRep.lastName }]) : await this.initialIntakeRepStore.filterAsync();
        data.insuranceReviewRep ? this.insuranceReviewRepStore.setItems([{ id: data.insuranceReviewRep.id, name: data.insuranceReviewRep.firstName + ' ' + data.insuranceReviewRep.lastName }]) : await this.insuranceReviewRepStore.filterAsync();
        data.confirmingRep ? this.confirmingRepStore.setItems([{ id: data.confirmingRep.id, name: data.confirmingRep.firstName + ' ' + data.confirmingRep.lastName }]) : await this.confirmingRepStore.filterAsync();
        data.purchaseRep ? this.purchaseRepStore.setItems([{ id: data.purchaseRep.id, name: data.purchaseRep.firstName + ' ' + data.purchaseRep.lastName }]) : await this.purchaseRepStore.filterAsync();

        data.corporate_ ? this.corporateStore.setItems([{ ...data.corporate_ }]) : await this.corporateStore.filterAsync();
        data.branch ? this.branchStore.setItems([{ ...data.branch }]) : await this.branchStore.filterAsync();

        data.spikeSet_ ? this.spikeSetStore.setItems([{ id: data.spikeSet_.id, name: data.spikeSet_.itemId }]) : await this.spikeSetStore.filterAsync();
        data.syringe_ ? this.syringeStore.setItems([{ id: data.syringe_.id, name: data.syringe_.itemId }]) : await this.syringeStore.filterAsync();
        data.drainageBag_ ? this.drainageBagStore.setItems([{ id: data.drainageBag_.id, name: data.drainageBag_.itemId }]) : await this.drainageBagStore.filterAsync();
        await this.pumpBrandStore.filterAsync();
    }

    @action.bound
    async getLookups() {
        const result = await this.lookupService.getLookups('DefaultItemType,PumpBrand');

        this.defaultItemType = result.defaultItemType;
        this.pumpBrand = _.orderBy(result.pumpBrand, ['name'], ['asc']);
    }

    @action.bound
    async onActivateAditionalContact(additionalContact) {
        try {
            additionalContact.inActive = !additionalContact.inActive;
            if (!additionalContact.inActive && additionalContact.deliveryTicketType === 'To' && this.additionalContacts.filter(a => a.deliveryTicketType === 'To' && !a.inActive && additionalContact.id !== a.id).length > 0){
                additionalContact.deliveryTicketType = 'CC';
                this.rootStore.notificationStore.error("Unable to set Email settings to “To”, since the Delivery Ticket is already being sent “To” a different active contact. Email setting has been updated to “CC”.");
            }
            await this.facilityAdditionalContactService.update(additionalContact);
            await this.onSuccessUpdateAditionalContact();
        }
        catch (err) {
            return this.onUpdateError(err);
        }
    }

    @action.bound
    onAddAditionalContact(additionalContact) {
        this.contactEditModal.open({
            item: additionalContact,
            onSuccess: this.onSuccessUpdateAditionalContact,
            additionalContacts: this.additionalContacts
        });
    }

    @action.bound
    openBulkEmrModal() {
        this.bulkEmrModal.open({
            emr: this.item.emr,
            //emrAccountName: this.item.emrAccountName,
            emrUserName: this.item.emrUserName,
            emrPassword: this.item.emrPassword,
            corporateId: this.item.corporateId,
            updateEmrFields: this.updateEmrFields
        });
    }

    @action.bound
    updateEmrFields(emrItem) {
        this.form.set({
            emr: emrItem.emr,
            //emrAccountName: emrItem.emrAccountName,
            emrUserName: emrItem.emrUserName,
            emrPassword: emrItem.emrPassword
        });

        this.item.emr = emrItem.emr;
        //this.item.emrAccountName = emrItem.emrAccountName;
        this.item.emrUserName = emrItem.emrUserName;
        this.item.emrPassword = emrItem.emrPassword;
    }

    @action.bound
    onBulkCreateAditionalContact(additionalContact) {
        this.additionalContactBulkCreateModal.open({
            item: additionalContact,
            onSuccess: this.onSuccessUpdateAditionalContact
        });
    }

    @action.bound
    openCorporate() {
        const corpId = this.form.$('corporateId').value;
        if (corpId)
            window.open(this.rootStore.routerStore.findRoute("master.app.main.corporate.preview").pattern.replace(":id", corpId));
    }

    @action.bound
    async onDeleteAdditionalContact(contact) {
        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            "Are you sure you want to delete the selected contact?",
            async () => {
                try {
                    await this.facilityAdditionalContactService.delete(contact);
                    await this.onSuccessUpdateAditionalContact();
                    await setTimeout(() => this.rootStore.notificationStore.success("Contact successfully removed."));
                } catch (err) {
                    notificationStore.error('An error ocurred while deleting the contact!', err);
                }
            }
        );

    }

    @action.bound
    async onSuccessUpdateAditionalContact() {
        let result = await this.facilityAdditionalContactService.find({ rpp: 100, sort: "inActive|asc,name|asc", facilityId: this.id, embed: "facilityAdditionalContactContactDepartments" });
        result = _.orderBy(result.data.item, ["inActive", c => c.name.toLowerCase()], "asc");
        const mappedContacts = result.map(additionalContacts => {
            let contactDepartments = additionalContacts.facilityAdditionalContactContactDepartments &&
                additionalContacts.facilityAdditionalContactContactDepartments.map(i => i.contactDepartment.name).join(", ");
            return {
                ...additionalContacts,
                departments: contactDepartments
            }
        });
        runInAction(() => {
            this.additionalContacts = mappedContacts;
        });
    }

    @action.bound
    async attachCoverageTypeStore(item) {
        let id = item.id;
        item.coverageTypeStore = new BaasicDropdownStore({}, {
            fetchFunc: async (term) => {
                const response = await this.coverageTypeService.find({ searchQuery: term, rpp: 20, fields: 'id,name' });
                return response.data.item;
            },
            onChange: (coverageType) => this.setCoverageType(coverageType, id)
        });
    }

    @action.bound
    setCoverageType(coverageType, id) {
        this.form.fields.get('areaOfCares').fields.get('item-' + id).fields.get('coverageTypeId').set(coverageType);
    }

    @action.bound
    async getResource(id) {
        // super swaps this.item for the fetched one, and the areas it carries only get their coverage-type
        // store in the loop below. On a re-fetch the rows are already on screen and would render for a moment
        // against a store that is not there yet, so take them down until the loop has run.
        this.areasConstructed = false;

        await super.getResource(id);

        var areaOfCareField = this.form.fields.get('areaOfCares');
        for (const item of this.item.facilityAreaOfCares) {
            var field = {
                name: 'item-' + item.id,
                fields: [{
                    name: 'coverageTypeId',
                    label: 'CoverageType',
                    placeholder: 'Select coverage type'
                }]
            };
            areaOfCareField.add(field);
            if (item.coverageTypeId) {
                this.setCoverageType(item.coverageTypeId, item.id);
            }
            await this.attachCoverageTypeStore(item);
        }

        runInAction(() => {
            this.areasConstructed = true;
        });
    }

    @action.bound
    createPersonDropdownStore(onChange) {
        return new BaasicDropdownStore({
            initFetch: false,
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.personService.find({ searchQuery: term, rpp: 20, fields: "id,firstName,lastName", sort: 'lastName|asc', isActive: true });
                return _.map(response.data.item, item => {
                    return {
                        id: item.id,
                        name: item.firstName + ' ' + item.lastName

                    };
                });
            },
            onChange: (person) => onChange(person)
        });
    }

    @action.bound
    createESignPreferenceDropdownStore(onChange) {
        return new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.eSignPreferenceService.find({ searchQuery: term });
                return _.map(response.data.item, item => {
                    return {
                        id: item.id,
                        name: item.name
                    };
                });
            },
            onChange: (eSign) => onChange(eSign)
        });
    }

    @action.bound
    onAccountRepChange(person) {
        this.form.set({ isNewFlow2: person !== this.settings.newFlowAccountRepId });
        this.item.accountRep = person;
        this.form.set({ accountRep: person });
    }

    @action.bound
    onPurchaseRepChange(person) {
        this.item.purchaseRep = person;
        this.form.set({ purchaseRep: person });
    }
    @action.bound
    onIntakeRepChange(person) {
        this.item.intakeRep = person;
        this.form.set({ intakeRep: person });
    }
    @action.bound
    onWoundCareRepChange(person) {
        this.item.woundCareRep = person;
        this.form.set({ woundCareRep: person });
    }
    @action.bound
    onInitialIntakeRepChange(person) {
        this.item.initialIntakeRep = person;
        this.form.set({ initialIntakeRep: person });
    }
    @action.bound
    onInsuranceReviewRepChange(person) {
        this.item.insuranceReviewRep = person;
        this.form.set({ insuranceReviewRep: person });
    }
    @action.bound
    onConfirmingRepChange(person) {
        this.item.confirmingRep = person;
        this.form.set({ confirmingRep: person });
    }
    @action.bound
    onPayorRepChange(person) {
        this.item.payorRep = person;
        this.form.set({ payorRep: person });
    }
    @action.bound
    onDocumentRepChange(person) {
        this.item.documentRep = person;
        this.form.set({ documentRep: person });
    }
    @action.bound
    onBillingRepChange(person) {
        this.item.billingRep = person;
        this.form.set({ billingRep: person });
    }
    @action.bound
    eSignPreferenceChange(eSign) {
        this.item.eSignPreference = eSign;
        this.form.set({ eSignPreference: eSign });
    }

    @action.bound
    async onInit({ initialLoad }) {
        if (!initialLoad) {
            this.rootStore.routerStore.goBack();
        }
    }

    @action.bound
    async saveResource() {
        // ISG-2961: Pricing Group is read-only while NikoHealth pricing groups are unavailable.
        // const selectedId = this.form.$("pricingGroupId").value;
        // if (!selectedId) {
        //     this.form.$("pricingGroupId").validate();
        //     this.form.$("pricingGroupId").showErrors(true);
        //     return;
        // }
        // const exists = this.pricingGroupStore.items.some(
        //     item => item.id === selectedId
        // );
        // if (!exists) {
        //     this.form.$("pricingGroupId").set(null);
        //     this.form.$("pricingGroupId").validate();
        //     this.form.$("pricingGroupId").showErrors(true);
        //     return;
        // }
        this.isSavingResource = true;
        const resource = this.form.values();
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }
            await this.actions.update({
                id: this.id,
                ...resource
            });

            const corporateChanged = this.isCorporateChanged(resource);

            await this.getResource(this.id);

            if (corporateChanged) {
                this.openPayerValidationModal(true);
            }

            this.form.setFieldsDisabled(false);

            await setTimeout(() => this.rootStore.notificationStore.success("FACILITY.EDIT.FACILITY_SUCCESS"), 10);
            this.isSavingResource = false;
            this.isChanged = false;
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            this.isSavingResource = false;
            return this.onUpdateError(err);
        }
    }

    @action.bound
    isCorporateChanged(resource) {
        return this.item.isNiko && resource.corporateId !== this.item.corporateId;
    }

    // The action-bar button opens the same pop-up with no corporate change behind it, so the loop can be re-run
    // on demand - after a mistyped payer name in Niko, or after a corporate change the user left unresolved.
    @action.bound
    openPayerValidationModal(corporateChanged = false) {
        this.facilityPayerValidationModal.open({
            facilityId: this.id,
            facilityName: this.item.name,
            corporateChanged: corporateChanged,
            onValidated: () => {
                this.item.payersNeedAttention = false;
            }
        });
    }

    @action.bound
    async updateResource(resource) {
        // ISG-2961: Pricing Group is read-only while NikoHealth pricing groups are unavailable.
        // const selectedId = this.form.$("pricingGroupId").value;
        // if (!selectedId) {
        //     this.form.validate();
        //     return;
        // }
        // const exists = this.pricingGroupStore.items.some(
        //     item => item.id === selectedId
        // );
        // if (!exists) {
        //     this.form.$("pricingGroupId").set(null);
        //     this.form.validate();
        //     return;
        // }
        this.isSavingResource = true;
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }
            await this.actions.update({
                id: this.id,
                ...resource
            });

            const corporateChanged = this.isCorporateChanged(resource);

            this.form.setFieldsDisabled(false);

            await setTimeout(() => this.rootStore.notificationStore.success("FACILITY.EDIT.FACILITY_SUCCESS"), 10);
            this.isSavingResource = false;
            this.isChanged = false;

            // Going back would take the pop-up with it, so leave the user on the page to work through it - and
            // re-read the facility first, so the page and the pop-up name it as it was just saved rather than
            // as it was loaded. Nothing reads the item on the way out, so the other branch skips the fetch.
            if (corporateChanged) {
                await this.getResource(this.id);
                this.openPayerValidationModal(true);
            } else {
                this.rootStore.routerStore.goBack();
            }
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            this.isSavingResource = false;
            return this.onUpdateError(err);
        }
    }

    @action.bound
    async syncAddress() {
        this.isAddressSynchronization = true;
        this.form.setFieldsDisabled(true);
        try {
            await this.actions.syncAddress(this.id);
            var item = await this.service.get(this.id);
            this.form.$("address").value = item.data.address;

            this.form.setFieldsDisabled(false);
            this.isAddressSynchronization = false;
            await setTimeout(() => this.rootStore.notificationStore.success("FACILITY.EDIT.FACILITY_SYNC_ADDRESS_SUCCESS"), 10);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            this.isAddressSynchronization = false;
            return this.onUpdateError(err);
        }
    }

    @action.bound
    async syncNikoPriceGroups() {
        this.isSyncing = true;
        const lookupService = new LookupService(
            this.rootStore.application.baasic.apiClient
        );
        await lookupService.syncNikoPriceGroups();
        await this.pricingGroupStore.filterAsync();
        this.isSyncing = false;
    }
}

export default FacilityEditViewStore;
